import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Crown, TrendingUp, TrendingDown, ArrowRight, Trophy } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

import { API_BASE } from "../lib/api"; 
import { safeJson } from "../lib/api";

const CARD_TXT = {
  es: { pro_unlocked: "PRO desbloqueado", pro_try: "Estrena Simulator", pro_title: "Estrena el Simulator", free_title: "Prueba el Simulator gratis", last_rank: "Tu último rango" },
  en: { pro_unlocked: "PRO unlocked", pro_try: "Try the Simulator", pro_title: "Try the Simulator", free_title: "Try the Simulator for free", last_rank: "Your latest rank" },
  sk: { pro_unlocked: "PRO odomknuté", pro_try: "Vyskúšaj Simulator", pro_title: "Vyskúšaj Simulator", free_title: "Vyskúšaj Simulator zadarmo", last_rank: "Tvoja posledná hodnosť" },
  de: { pro_unlocked: "PRO freigeschaltet", pro_try: "Simulator ausprobieren", pro_title: "Simulator ausprobieren", free_title: "Simulator kostenlos testen", last_rank: "Dein letzter Rang" },
  "zh-CN": { pro_unlocked: "PRO 已解锁", pro_try: "试用 Simulator", pro_title: "试用 Simulator", free_title: "免费试用 Simulator", last_rank: "你最新的排名" },
};

const RANK_COLORS = {
  "RETAIL SURVIVOR": { border: "border-rose-400/40", text: "text-rose-300", glow: "shadow-[0_0_18px_rgba(255,51,85,0.25)]" },
  "STRUCTURE READER": { border: "border-amber-400/40", text: "text-amber-300", glow: "shadow-[0_0_18px_rgba(245,166,35,0.25)]" },
  "LIQUIDITY HUNTER": { border: "border-cyan-400/40", text: "text-cyan-300", glow: "shadow-[0_0_18px_rgba(0,212,255,0.25)]" },
  "SMART MONEY ANALYST": { border: "border-purple-400/40", text: "text-purple-300", glow: "shadow-[0_0_18px_rgba(155,89,255,0.25)]" },
  "INSTITUTIONAL OPERATOR": { border: "border-emerald-400/40", text: "text-emerald-300", glow: "shadow-[0_0_18px_rgba(0,230,118,0.25)]" },
  "MARKET PREDATOR": { border: "border-amber-300/60", text: "text-amber-200", glow: "shadow-[0_0_22px_rgba(255,215,0,0.45)]" },
};

/**
 * Mini-card surfaced in the AcademyPage header.
 * - PRO user with sessions → "Tu último rango: SMART MONEY ANALYST · jugar de nuevo"
 * - PRO user no sessions   → "Estrena el Simulator →"
 * - FREE user              → "Prueba el Simulator gratis →" (1 simulación trial)
 * - Guest (no auth)        → "Descubre el Simulator →"
 */
export const SimulatorRankCard = () => {
  const { user, isPro } = useAuth();
  const { lang } = useLanguage();
  const cTxt = CARD_TXT[lang] || CARD_TXT.en || CARD_TXT.es;
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Only fetch when authenticated PRO user (only case backend has data)
      if (user === null || user === false || !isPro) {
        setLoaded(true);
        return;
      }
      try {
        const API = API_BASE;  // runtime-resolved, see /lib/api.js
        const token = localStorage.getItem("rebel_jwt");
        const res = await fetch(`${API}/api/simulator/last-rank`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setData(await safeJson(res));
      } catch {
        /* silent */
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [user, isPro]);

  if (!loaded) return null;

  // PRO with rank
  if (isPro && data?.rank) {
    const r = RANK_COLORS[data.rank] || RANK_COLORS["LIQUIDITY HUNTER"];
    const positive = (data.pnl_pct || 0) >= 0;
    return (
      <Link
        to="/simulator/play"
        data-testid="academy-sim-rank-card"
        title={`${cTxt.last_rank}: ${data.rank}`}
        className={`group relative inline-flex shrink-0 items-center gap-2 overflow-hidden rounded-2xl border bg-black/40 p-2 backdrop-blur-xl transition-all hover:-translate-y-0.5 sm:max-w-none sm:gap-3 sm:px-4 sm:py-2.5 ${r.border} ${r.glow}`}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] sm:h-9 sm:w-9">
          <Crown className={`h-4 w-4 ${r.text}`} />
        </div>
        <div className="hidden min-w-0 leading-tight sm:block">
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/45">
            {cTxt.last_rank}
          </p>
          <p className={`mt-0.5 truncate font-display text-sm font-black tracking-tight ${r.text}`}>
            {data.rank}
          </p>
        </div>
        <span
          className={`hidden font-mono text-xs sm:inline-block ${
            positive ? "text-emerald-300" : "text-rose-300"
          }`}
        >
          {positive ? <TrendingUp className="inline h-3 w-3" /> : <TrendingDown className="inline h-3 w-3" />}
          <span className="ml-1">
            {positive ? "+" : ""}
            {(data.pnl_pct || 0).toFixed(1)}%
          </span>
        </span>
        <ArrowRight className="hidden h-3.5 w-3.5 text-white/55 transition-transform group-hover:translate-x-1 sm:inline-block" />
      </Link>
    );
  }

  // PRO but no session yet
  if (isPro) {
    return (
      <Link
        to="/simulator/play"
        data-testid="academy-sim-cta-pro"
        title={cTxt.pro_title}
        className="group inline-flex shrink-0 items-center gap-2 rounded-2xl border border-emerald-400/40 bg-emerald-400/[0.06] p-2 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-emerald-400/70 hover:bg-emerald-400/15 hover:shadow-[0_0_24px_rgba(0,230,118,0.3)] sm:max-w-none sm:px-4 sm:py-2.5"
      >
        <Zap className="h-4 w-4 shrink-0 text-emerald-300" />
        <div className="hidden min-w-0 leading-tight sm:block">
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-200/70">{cTxt.pro_unlocked}</p>
          <p className="truncate font-display text-sm font-bold tracking-tight text-emerald-200">
            {cTxt.pro_try}
          </p>
        </div>
        <ArrowRight className="hidden h-3.5 w-3.5 text-emerald-300 transition-transform group-hover:translate-x-1 sm:inline-block" />
      </Link>
    );
  }

  // FREE / guest
  return (
    <Link
      to="/simulator"
      data-testid="academy-sim-cta-free"
      title={cTxt.free_title}
      className="group inline-flex shrink-0 items-center gap-2 rounded-2xl border border-cyan-400/40 bg-cyan-400/[0.05] p-2 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-cyan-400/70 hover:bg-cyan-400/15 hover:shadow-[0_0_24px_rgba(0,212,255,0.3)] sm:max-w-none sm:px-4 sm:py-2.5"
    >
      <Trophy className="h-4 w-4 shrink-0 text-cyan-300" />
      <div className="hidden min-w-0 leading-tight sm:block">
        <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-cyan-200/70">RE₿EL Market Simulator™</p>
        <p className="truncate font-display text-sm font-bold tracking-tight text-cyan-100">
          {cTxt.free_title}
        </p>
      </div>
      <ArrowRight className="hidden h-3.5 w-3.5 text-cyan-300 transition-transform group-hover:translate-x-1 sm:inline-block" />
    </Link>
  );
};

export default SimulatorRankCard;
